<script lang="ts">
  import { debounce } from 'lodash-es'
  import { tick } from 'svelte'
  import app from '#app/app'
  import { feedsAPI } from '#app/lib/api/feeds'
  import Flash from '#app/lib/components/flash.svelte'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import { screen } from '#app/lib/components/stores/screen'
  import { icon } from '#app/lib/icons'
  import { imgSrc } from '#app/lib/image_source'
  import { onlyHeadMetadataRequired, type MetadataUpdate } from '#app/lib/metadata/update'
  import { promisePlaceholder } from '#app/lib/promises'
  import { userContent } from '#app/lib/user_content'
  import { isOpenedOutside, loadInternalLink } from '#app/lib/utils'
  import { commands } from '#app/radio'
  import type { ClickEvent } from '#app/types/declarations/svelte'
  import Spinner from '#components/spinner.svelte'
  import GroupActions from '#groups/components/group_actions.svelte'
  import { getCachedSerializedGroupMembers, getAllGroupMembersIds, type SerializedGroup, type SerializedGroupUser } from '#groups/lib/groups'
  import InventoryBrowser from '#inventory/components/inventory_browser.svelte'
  import { getInventoryView } from '#inventory/components/lib/inventory_browser_helpers'
  import type { FocusedSection, ProfileSection } from '#inventory/types/inventory_browser'
  import UsersListings from '#listings/components/users_listings.svelte'
  import { i18n, I18n } from '#user/lib/i18n'
  import AdminInfo from '#users/components/admin_info.svelte'
  import ProfileNav from '#users/components/profile_nav.svelte'
  import UserProfile from '#users/components/user_profile.svelte'
  import UsersHomeSectionList from '#users/components/users_home_section_list.svelte'

  let {
    group = $bindable(),
    profileSection = $bindable(null),
    standalone = false,
    focusedSection = $bindable(),
    unselectGroup,
  }: {
    group: SerializedGroup
    profileSection?: ProfileSection
    standalone?: boolean
    focusedSection: FocusedSection
    unselectGroup?: () => void
  } = $props()

  let members: SerializedGroupUser[] = $state()
  let flash: FlashState = $state()

  const waitForMembers = onlyHeadMetadataRequired ?
    promisePlaceholder() :
      getCachedSerializedGroupMembers(group)
        .then(users => members = users)
        .catch(err => flash = err)

  const groupId = $derived(group._id)
  const name = $derived(group.name)
  const description = $derived(group.description)
  const requested = $derived(group.requested)

  const settingsPathname = $derived(group.settingsPathname)
  const mainUserIsMember = $derived(group.mainUserIsMember)
  const mainUserIsAdmin = $derived(group.mainUserIsAdmin)
  const picture = $derived(group.picture)

  function showMembersMenu (e: ClickEvent) {
    if (isOpenedOutside(e)) return
    commands.execute('show:group:board', group, { openedSection: 'groupInvite' })
    e.preventDefault()
  }

  let selectedMember: SerializedGroupUser = $state()
  let groupProfileEl: HTMLElement = $state()
  function onSelectMember (member: SerializedGroupUser) {
    selectedMember = member
    focusedSection = { type: 'user' }
  }
  function unselectMember () {
    selectedMember = null
    focusedSection = { type: 'group' }
  }

  async function onFocus () {
    if (!groupProfileEl) await tick()
    // Let app.navigate scroll to the page top when GroupProfile
    // is already at the top itself (standalone mode), to make the UsersHomeNav visible
    const pageSectionElement = standalone ? null : groupProfileEl
    let pathname, title, rss
    title = `${name} - ${I18n('group')}`
    if (profileSection === 'inventory') {
      pathname = group.inventoryPathname
      title += ` - ${i18n('Inventory')}`
      rss = feedsAPI('group', groupId)
    } else if (profileSection === 'listings') {
      pathname = group.listingsPathname
      title += ` - ${i18n('Lists')}`
    } else {
      pathname = group.pathname
      rss = feedsAPI('group', groupId)
    }
    const metadata: MetadataUpdate = {
      title,
      description,
      image: picture,
      rss,
    }
    app.navigate(pathname, { pageSectionElement, metadata })
  }
  const debouncedOnFocus = debounce(onFocus, 200, { leading: true })

  $effect(() => {
    if (focusedSection.type === 'group') debouncedOnFocus()
  })
</script>

<div class="full-group-profile">
  <div class="group-profile" bind:this={groupProfileEl}>
    <div class="group-profile-header">
      <div class="section-one">
        <div class="cover-header">
          {#if picture}
            <img class="cover-image" src={imgSrc(picture, 1600)} alt="" />
          {/if}
          <div class="info">
            <span class="name">{name}</span>
          </div>
          <div class="icon-buttons">
            {#if !standalone && $screen.isSmallerThan('$smaller-screen')}
              <button class="unselect-group" onclick={unselectGroup}>{@html icon('times')}</button>
            {/if}
            {#if mainUserIsMember}
              <a
                class="show-group-settings"
                href={settingsPathname}
                title={i18n('settings')}
                onclick={loadInternalLink}
              >
                {@html icon('cog')}
                {#if mainUserIsAdmin && requested.length > 0}
                  <span class="counter">{requested.length}</span>
                {/if}
              </a>
            {/if}
          </div>
        </div>
        {#if description}
          <p class="description">{@html userContent(description)}</p>
        {/if}
        <AdminInfo id={group._id} created={group.created} />
      </div>

      <div class="section-two" class:has-unselect-button={!standalone}>
        {#if !standalone && $screen.isLargerThan('$smaller-screen')}
          <button class="unselect-group" onclick={unselectGroup}>{@html icon('times')}</button>
        {/if}

        <div class="list-label-wrapper">
          <p class="list-label">{I18n('members')}</p>
          {#if mainUserIsMember}
            <a
              class="show-members-menu tiny-button light-blue"
              href={settingsPathname}
              onclick={showMembersMenu}
            >
              {@html icon('plus')}
              {i18n('invite')}
            </a>
          {/if}
        </div>
        <div class="members-list">
          {#await waitForMembers}
            <Spinner />
          {:then}
            <UsersHomeSectionList
              docs={members}
              type="members"
              {group}
              onSelectDoc={({ doc }) => onSelectMember(doc as SerializedGroupUser)}
            />
          {/await}
        </div>
      </div>
    </div>

    <div class="group-actions">
      <GroupActions bind:group />
    </div>
  </div>

  <Flash state={flash} />

  {#if selectedMember}
    <!-- Recreate component when selectedMember changes, see https://svelte.dev/docs#template-syntax-key -->
    {#key selectedMember}
      <UserProfile
        user={selectedMember}
        {groupId}
        {focusedSection}
        onUnselectProfile={unselectMember}
      />
    {/key}
  {:else if !onlyHeadMetadataRequired}
    <ProfileNav {group} bind:profileSection {focusedSection} />
    {#if profileSection === 'listings'}
      <UsersListings usersIds={getAllGroupMembersIds(group)} />
    {:else}
      <InventoryBrowser
        inventoryViewPromise={getInventoryView('group', group)}
        {groupId}
      />
    {/if}
  {/if}
</div>

<style lang="scss">
  @import "general/scss/utils";
  .full-group-profile{
    // Make sure it is possible to scroll to put the group profile at the top of the viewport
    min-height: 100vh;
  }
  .group-profile{
    background-color: #eee;
    margin: 0.5em 0;
  }
  .section-one{
    flex: 1 0 auto;
  }
  .cover-header{
    max-width: 100%;
    @include display-flex(column, center, center);
    height: 18em;
    position: relative;
  }
  .icon-buttons{
    @include position(absolute, 3px, 3px);
    @include display-flex(column, center, space-between);
    @include radius;
    font-size: 1.4em;
    a{
      padding: 0.3em;
      @include bg-hover($coverTextBg);
      :global(.fa){
        color: white;
        width: 2em;
        text-align: center;
      }
    }
    .unselect-group{
      color: white;
    }
  }
  .info{
    // Somehow needed to make it appear above group-cover-picture and picture-color-filter
    position: relative;
    align-self: stretch;
    @include display-flex(column, center, center);
    color: white;
    :global(.fa){
      color: white;
    }
    flex: 1;
  }
  .cover-image{
    @include position(absolute, 0, 0, 0, 0);
  }
  .section-two{
    flex: 2 0 auto;
    @include display-flex(column, stretch);
  }
  .unselect-group{
    align-self: flex-end;
    padding: 0.2rem;
    :global(.fa){
      @include shy;
      font-size: 2rem;
    }
  }
  .show-group-settings{
    position: relative;
    .counter{
      @include counter-commons;
      @include position(absolute, 2px, 2px);
      font-size: 1rem;
      line-height: 1.1rem;
    }
  }
  .description{
    padding: 0.5em;
    overflow: auto;
  }
  .show-members-menu{
    margin: 0 1em;
  }
  .members-list{
    max-height: 20em;
    overflow: auto;
  }
  .name{
    text-align: center;
    font-size: 1.5em;
    @include text-wrap;
    max-width: 100%;
    color: white;
    margin: auto;
    font-weight: bold;
    @include serif;
    padding: 0.5em 1em;
    @include radius;
    background-color: rgba($dark-grey, 0.9);
  }
  .group-actions{
    :global(button){
      margin-block: 1em;
    }
    :global(.requested){
      text-align: center;
      opacity: 0.8;
    }
  }

  .list-label-wrapper{
    @include display-flex(row, flex-end, space-between);
    padding: 0.5em 0;
  }
  .has-unselect-button .list-label-wrapper{
    padding: 0 0 0.5em;
  }

  /* Small screens */
  @media screen and (width < $small-screen){
    .group-profile{
      @include display-flex(column, stretch, center);
    }
    .section-two{
      padding: 1em;
    }
    .members-list{
      :global(.users-home-section-list){
        justify-content: flex-start;
      }
    }
    .description{
      max-height: 10em;
      padding: 0.5em;
    }
    .list-label{
      margin-block-end: 0.5em;
      font-size: 1.2em;
    }
    .list-label-wrapper{
      text-align: center;
      @include display-flex(column, center, center);
      flex-direction: column;
    }
  }

  /* Large screens */
  @media screen and (width >= $small-screen){
    .group-profile-header{
      @include display-flex(row, flex-start, space-between);
    }
    .section-one{
      flex: 1 0 20em;
      max-width: 50%;
    }
    .section-two{
      flex: 5 0 0;
      .list-label{
        text-align: end;
        margin: 0.5em 1em;
      }
    }
    .group-actions{
      @include display-flex(column, center, center);
      margin-block-start: 1em;
      :global(.restrictions){
        margin-block-start: 1em;
      }
    }
    .members-list{
      margin-inline-start: 0.8em;
    }
  }

  /* Very Large screens */
  @media screen and (width >= 1200px){
    .section-two{
      max-width: 70vw;
    }
  }
</style>
